import { useQuery } from '@tanstack/react-query';
import { MapPin } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { api } from '../lib/api';
import { formatDateTime, formatUtcDate, personName } from '../lib/format';
import { localToday } from '../lib/locations';
import type { AssetOnDate, LocationPeriod, RoomAssignment } from '../lib/types';
import { Badge, Card, CardHeader, DescriptionList, EmptyState, Field, Input, Spinner } from './ui';

const names = (rows: RoomAssignment[]) => rows.map((row) => personName(row.person)).join(', ');

function OnDate({ data }: { data: AssetOnDate }) {
  if (!data.tracked) return <p className="mt-3 text-sm text-slate-500">It wasn’t in Edvora yet on {formatUtcDate(data.date)}.</p>;
  return (
    <div className="mt-4 rounded-lg bg-slate-50 p-4">
      <DescriptionList
        columns={2}
        items={[
          { label: 'Kept in', value: data.location?.label ?? 'No location recorded' },
          { label: 'Responsible for that room', value: data.location?.roomId ? names(data.responsibleForLocation) || 'No one' : null },
          { label: 'Checked out to', value: data.checkout?.assigneeLabel ?? 'Not checked out' },
          ...(data.checkout?.roomId ? [{ label: 'Responsible for that room', value: names(data.responsibleForCheckoutRoom) || 'No one' }] : []),
          ...(data.cartLease ? [{ label: 'Cart leased to', value: data.cartLease.assigneeLabel }] : []),
        ]}
      />
    </div>
  );
}

/** Where a device was kept over time, who was responsible for each room, and a lookup for any past day. */
export function LocationHistoryCard({ assetId }: { assetId: string }) {
  const [date, setDate] = useState('');
  const history = useQuery({
    queryKey: ['asset-locations', assetId],
    queryFn: () => api.get<LocationPeriod[]>(`/assets/${assetId}/location-history`),
  });
  const onDate = useQuery({
    queryKey: ['asset-locations', assetId, 'on', date],
    queryFn: () => api.get<AssetOnDate>(`/assets/${assetId}/on`, { date }),
    enabled: /^\d{4}-\d{2}-\d{2}$/.test(date),
  });

  return (
    <Card>
      <CardHeader title="Location history" description="Where it was kept, and who was responsible for the room at the time" />
      <div className="border-b border-slate-100 p-5">
        <Field label="Look up a day" hint="Where it was and who was responsible at the end of that day">
          <Input type="date" value={date} max={localToday()} onChange={(e) => setDate(e.target.value)} className="w-44" />
        </Field>
        {date && (onDate.isLoading ? <Spinner className="mt-3" /> : onDate.data && <OnDate data={onDate.data} />)}
      </div>
      {history.isLoading ? (
        <div className="p-5">
          <Spinner />
        </div>
      ) : !history.data?.length ? (
        <EmptyState icon={<MapPin />} title="No location recorded" />
      ) : (
        <ol className="space-y-4 p-5">
          {history.data.map((period) => (
            <li key={period.id} className="relative pl-5 text-sm">
              <span className={`absolute top-1.5 left-0 size-2 rounded-full ${period.endedAt ? 'bg-slate-300' : 'bg-brand-500'}`} />
              <p className="flex flex-wrap items-center gap-2 text-slate-800">
                {period.room ? (
                  <Link to={`/locations/rooms/${period.room.id}`} className="font-medium hover:text-brand-700">
                    {period.label}
                  </Link>
                ) : (
                  <span className="font-medium">{period.label}</span>
                )}
                {!period.endedAt && <Badge tone="brand">Now</Badge>}
                {period.building?.kind === 'WAREHOUSE' && <Badge tone="violet">Off campus</Badge>}
                {period.cart && (
                  <Link to={`/carts/${period.cart.id}`} className="text-xs text-brand-700 hover:underline">
                    Cart
                  </Link>
                )}
              </p>
              <p className="text-xs text-slate-500">
                {formatDateTime(period.startedAt)} – {period.endedAt ? formatDateTime(period.endedAt) : 'now'} · {period.reason}
                {period.changedByName && ` by ${period.changedByName}`}
              </p>
              {period.roomId && (
                <p className="text-xs text-slate-600">Responsible: {period.responsible.length ? names(period.responsible) : 'no one on record'}</p>
              )}
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
}
